import { Box, Typography } from '@mui/material';
import { Leaf } from 'lucide-react';
import MediaGallery from './MediaGallery';

function Marker({ status }) {
  const base = {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: '2px solid',
    zIndex: 1,
    position: 'relative',
  };

  if (status === 'completed') {
    return (
      <Box
        sx={{
          ...base,
          backgroundColor: 'primary.main',
          borderColor: 'primary.main',
          color: '#fff',
        }}
      >
        <Leaf size={18} strokeWidth={2} />
      </Box>
    );
  }

  if (status === 'in-progress') {
    return (
      <Box
        sx={{
          ...base,
          backgroundColor: '#fff',
          borderColor: 'primary.main',
          color: 'primary.main',
          boxShadow: '0 0 0 4px rgba(74, 106, 74, 0.15)',
        }}
      >
        <Leaf size={18} strokeWidth={2} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...base,
        backgroundColor: 'rgba(255,255,255,0.8)',
        borderColor: 'rgba(74, 106, 74, 0.35)',
        borderStyle: 'dashed',
        color: 'rgba(74, 106, 74, 0.45)',
      }}
    >
      <Leaf size={16} strokeWidth={1.75} />
    </Box>
  );
}

function Milestone({ milestone, isLast }) {
  const plannedDim = milestone.status === 'planned' ? 0.75 : 1;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '36px 1fr',
        columnGap: { xs: 2, sm: 3 },
        position: 'relative',
        pb: isLast ? 0 : { xs: 4, sm: 5, md: 6 },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Marker status={milestone.status} />
        {!isLast && (
          <Box
            sx={{
              position: 'absolute',
              top: 40,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '2px',
              bottom: -8,
              backgroundColor: 'rgba(74, 106, 74, 0.3)',
              borderLeft: milestone.status === 'planned' ? '2px dashed rgba(74, 106, 74, 0.35)' : 'none',
              backgroundImage:
                milestone.status === 'planned'
                  ? 'none'
                  : undefined,
            }}
          />
        )}
      </Box>

      <Box sx={{ opacity: plannedDim, pt: 0.25 }}>
        <Typography
          sx={{
            color: 'primary.main',
            fontSize: { xs: '0.78rem', sm: '0.82rem' },
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          {milestone.dateLabel}
        </Typography>
        <Typography
          component="h3"
          sx={{
            fontFamily: 'var(--font-heading)',
            color: 'text.primary',
            fontSize: { xs: '1.35rem', sm: '1.55rem', md: '1.75rem' },
            fontWeight: 500,
            lineHeight: 1.2,
            mb: 1,
          }}
        >
          {milestone.title}
        </Typography>
        {milestone.description && (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.95rem', sm: '1rem' },
              lineHeight: 1.65,
              maxWidth: '42rem',
            }}
          >
            {milestone.description}
          </Typography>
        )}
        <MediaGallery media={milestone.media} />
      </Box>
    </Box>
  );
}

function RoadmapTimeline({ roadmap }) {
  if (!roadmap?.milestones?.length) return null;

  return (
    <Box sx={{ mb: { xs: 5, sm: 7, md: 9 } }}>
      {roadmap.title && (
        <Typography
          component="h2"
          sx={{
            fontFamily: 'var(--font-heading)',
            color: 'text.primary',
            fontSize: { xs: '1.9rem', sm: '2.4rem', md: '2.75rem' },
            fontWeight: 500,
            lineHeight: 1.15,
            textAlign: 'center',
            mb: { xs: 4, sm: 5, md: 6 },
          }}
        >
          {roadmap.title}
        </Typography>
      )}
      <Box
        sx={{
          maxWidth: 760,
          mx: 'auto',
          px: { xs: 0.5, sm: 2 },
        }}
      >
        {roadmap.milestones.map((milestone, i) => (
          <Milestone
            key={milestone.id || i}
            milestone={milestone}
            isLast={i === roadmap.milestones.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
}

export default RoadmapTimeline;
